<template>
  <transition name="fade" mode="out-in">
    <div v-if="props?.space" class="space-management-wrapper">
      <div class="space-header">
        <div class="title">{{ $t('app.panels.spaceManagement.title') }}</div>
        <div class="separator"></div>
        <div class="section-title">{{ $t('app.panels.spaceManagement.subtitle') }}</div>
        <div class="texts">
          <span class="description">{{ $t('app.panels.spaceManagement.spaceName') }}</span>
          <ConfirmInput inputClass="outline-white sm-text sm" :modelValue="state.spaceName" @update:modelValue="updateSpaceName"></ConfirmInput>
        </div>
      </div>
      <div class="space-body">
        <div class="separator"></div>
        <div class="table-header">
          <div class="information">
            <!-- <i class="bi bi-person-gear"></i> -->
            <span class="section-title">{{ $t('app.spaces.management.adminList') }}</span>
          </div>
          <Button class="icon icon-left sm outline-white put-to-end" icon="bi-plus" :label="$t('app.spaces.management.addAdmin')" @click="emit('openSubPanel')"></Button>
        </div>
        <div class="admin-table">
          <AdministratorTable :workspace-id="space.workspace_id"></AdministratorTable>
          <!-- <Table
            v-if="!isRolesPending && !isUserDetailsPending"
            :data="spaceAdministrators"
            :fields="tableFields"
            :removeBulkActions="true"
            :rowClickToSelect="false"
            :itemsPerPage="100"
          >
            <template #[`email`]="admin: { data: MemberDetails, index: number }">
              <p class="email-text-length xs-text">
                {{ admin.data.account.personal_information.email }}
              </p>
            </template>
            <template #[`last_name`]="admin: { data: MemberDetails, index: number }">
              <p class="name-text-length xs-text">
                {{ admin.data.account.personal_information.last_name }}
              </p>
            </template>
            <template #[`first_name`]="admin: { data: MemberDetails, index: number }">
              <p class="name-text-length xs-text">
                {{ admin.data.account.personal_information.first_name }}
              </p>
            </template>
            <template #actions="admin: { row: MemberDetails, index: number }">
              <Button class="icon outline red square" icon="bi-trash3-fill" />
            </template>
          </Table> -->
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { useToast } from 'vue-toastification';

import Button from '@/components/global/Button.vue';
import ConfirmInput from '@/components/input/ConfirmInput.vue';
import AdministratorTable from '@/components/global/tables/AdministratorTable.vue';

import { MemberRole } from '@/types/role';
import { Space } from '@/types/space';

import { useDeleteMemberGrant, useMembersDetails } from '@/queries/members.query';
import { useAllOrgnisationRoles } from '@/queries/organisation.query';
import { useEditSpace } from '@/queries/space.query';

const toast = useToast();
const { t } = useI18n();

const appStore = useAppStore();

const { data: userDetails, isPending: isUserDetailsPending } = useMembersDetails();
const { data: allRoles, isPending: isRolesPending } = useAllOrgnisationRoles();

useDeleteMemberGrant();
const { mutate: editSpace } = useEditSpace();

const emit = defineEmits(['openSubPanel']);

const state = reactive<{
  isEditingName: boolean,
  spaceName: string,
  slidingPanelIsOpen: boolean,
}>({
  isEditingName: false,
  spaceName: "",
  slidingPanelIsOpen: false,
});

const props = defineProps<{
  space: Space,
}>();

function updateSpaceName(newSpaceName: string) {
  setTimeout(() => {
    state.isEditingName = false;
  }, 100);

  if (newSpaceName === props?.space?.name) return;
  editSpace({
    organisationId: appStore.currentOrganisationId,
    spaceId: props?.space?.workspace_id,
    name: newSpaceName,
    imageId: props?.space?.image_id,
  }, {
    onSuccess: () => {
      toast.success(t('app.toasts.success'));
    },
    onError: () => {
      toast.error(t('other.error.ERROR_UNKNOWN'));
    }
  });
}

onMounted(() => {
  state.spaceName = props?.space?.name;
});

watch(props.space, () => {
  state.spaceName = props?.space?.name;
})
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.inline {
  display: flex;
  gap: 4px;
  align-items: center;
}

.put-to-end {
  margin-left: auto;
}

.separator {
  width: 100%;
  height: 1px;
  background-color: $light-grey-color;
}

.email-text-length {
  max-width: 180px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap
}

.name-text-length {
  max-width: 100px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap
}

.space-management-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem;

  .space-header {
    display: flex;
    flex-direction: column;
    gap: 24px;
    //align-items: center;

    .title {
      font-size: 20px;
      font-weight: 700;
    }

    .logo-wrapper {
      position: relative;
      width: 90px;
      height: 90px;

      .logo {
        width: 90px;
        height: 90px;
      }

      .edit-logo {
        position: absolute;
        top: -12px;
        right: -12px;
      }
    }

    .texts {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 400px;

      .title {
        font-size: 26px;
      }

      .description {
        font-size: 12px;
        font-weight: 600;
        color: $secondary-200-color;
      }
    }
  }

  .section-title {
    font-size: 18px;
    font-weight: 600;
  }

  .space-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-top: 32px;

    .table-header {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-top: 24px;

      .information {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;

        border-radius: 4px;

        i {
          font-size: 20px;
        }
      }
    }

    .admin-table {
      display: flex;
      flex-direction: column;
      flex: 1;
      margin-top: 12px;
      margin-bottom: 12px;
    }
  }
}
</style>